import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Popover, PopoverContent, PopoverItem, PopoverTrigger } from "@shared/v2/Popover";
import Alert from "@shared/v2/Alert";
import Error from "@shared/Error";
import TextButton from "@shared/v2/TextButton";
import Button from "@shared/v2/Button/Button";
import { ArrowUpRightSolidV6, Check, PlusSolidV6 } from "@shared/v2/Icomoon/Icons";
import { getTitleAndEscrow } from "./actions/thunks";
import { useFeatureFlags } from "../../../reducers/layoutReducer/selectors";
import { SubmitReqState } from "./actions/creators";
import errorShape from "./model/error";
import Header from "../../UI/Header";
import TitleAndEscrowCard from "./TitleAndEscrowCard";
import PSABlueprintModal from "./PSABlueprintModal";
import PreContractBlueprintModal from "./PreContractBlueprintModal";

const TitleAndEscrow = ({
  error,
  toastState,
  onSubmitReqState,
  type,
  isDismiss,
  getTitleAndEscrow,
  listingId,
  title,
  useCustomTitle,
  streetAddress,
  city,
  locality,
  postalCode,
  lastSubmitted,
  titleType,
}) => {
  const [isPSAModalOpen, setPSAModalOpen] = useState(false);
  const [isPreContractModalOpen, setPreContractModalOpen] = useState(false);
  const featureFlags = useFeatureFlags();

  const checkFullAddress = (useCustomTitle ? title : streetAddress) && city && locality && postalCode;

  useEffect(() => {
    getTitleAndEscrow(listingId);
  }, [listingId]);

  const showModal = () => {
    onSubmitReqState({ isloading: false, isSucceed: false, isFailed: false, failureMsg: "" });
    setPSAModalOpen(true);
  };

  const onHideHandlr = () => {
    onSubmitReqState({ isloading: false, isSucceed: false, isFailed: false, failureMsg: "" });
    setPSAModalOpen(false);
  };

  const showPreContractModal = () => {
    onSubmitReqState({ isloading: false, isSucceed: false, isFailed: false, failureMsg: "" });
    setPreContractModalOpen(true);
  };

  const onHidePreContractHandlr = () => {
    onSubmitReqState({ isloading: false, isSucceed: false, isFailed: false, failureMsg: "" });
    setPreContractModalOpen(false);
  };
  return (
    <>
      {toastState?.show && toastState?.msg && (
        <Alert
          variant="success"
          containerClass="tw-z-1000 !tw-top-[100px] !tw-fixed !tw-right-3/12 !tw-left-4/12"
          text={
            <>
              <Check size="xl" className="tw-w-16px tw-h-16px tw-mb-[-4px]" />
              <span className="tw-capitalize tw-pl-[9px]">{toastState?.msg}</span>
            </>
          }
          role="alert"
        />
      )}

      <div className="tw-flex tw-flex-col tw-gap-[16px]">
        <Header header="Title and Escrow" />
        {error && <Error errorMessage={error.message} />}
        {!isDismiss && <TitleAndEscrowCard isBlueprintOnboarded={featureFlags?.blueprintTitle110} />}
        <div>
          {checkFullAddress && featureFlags?.blueprintTitle110 && type === "seller" ? (
            titleType === "pre-contract" ? (
              <>
                <Button
                  schema={isPSAModalOpen || isPreContractModalOpen ? "primary" : "secondary"}
                  onClick={showModal}
                  className="tw-mt-[4px] tw-w-full tw-h-[44px] !tw-rounded-[8px] tw-flex !tw-gap-[4px] tw-items-center tw-justify-center tw-text-14d !tw-normal-case"
                >
                  <PlusSolidV6 />
                  <span>Submit Purchase Agreement</span>
                </Button>
              </>
            ) : (
              <Popover placement="bottom-end">
                <PopoverTrigger>
                  <Button
                    schema={isPSAModalOpen || isPreContractModalOpen ? "primary" : "secondary"}
                    className="tw-mt-[4px] tw-w-full tw-h-[44px] !tw-rounded-[8px] tw-flex !tw-gap-[4px] tw-items-center tw-justify-center tw-text-14d !tw-normal-case"
                  >
                    <PlusSolidV6 />
                    <span>Open Title with Blueprint</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="tw-w-[245px]">
                  <PopoverItem onClick={showModal}>Purchase and Sale Agreement</PopoverItem>
                  <PopoverItem onClick={showPreContractModal}>New Listing (Pre-contract) </PopoverItem>
                </PopoverContent>
              </Popover>
            )
          ) : (
            <Button
              schema={isPSAModalOpen || isPreContractModalOpen ? "primary" : "secondary"}
              disabled={!checkFullAddress || !featureFlags?.blueprintTitle110}
              onClick={showModal}
              className="tw-mt-[4px] tw-w-full tw-h-[44px] !tw-rounded-[8px] tw-flex !tw-gap-[4px] tw-items-center tw-justify-center tw-text-14d !tw-normal-case"
            >
              <PlusSolidV6 />
              <span>Open Title with Blueprint</span>
            </Button>
          )}
        </div>
        {featureFlags?.blueprintTitle110 && !checkFullAddress && (
          <div className="tw-text-center tw-px-[8px] tw-text-12px tw-leading-[16px] tw-font-normal tw-text-gray-75">
            Please enter a full address for this transaction to start the title process
          </div>
        )}
        {featureFlags?.blueprintTitle110 && checkFullAddress && lastSubmitted && (
          <div className="tw-text-center tw-px-[8px] tw-text-12px tw-leading-[16px] tw-font-normal tw-text-gray-75">
            {titleType === "pre-contract" ? "Pre-contract Submitted:" : "Last Submitted:"} {lastSubmitted}
          </div>
        )}
        {featureFlags?.blueprintTitle110 && (
          <div className="tw-text-center">
            <TextButton size="small" onClick={() => window.open("https://app.blueprinttitle.com", "_blank")}>
              <span className="tw-pr-[4px]">Go to Blueprint Portal</span>
              <ArrowUpRightSolidV6 size="s" />
            </TextButton>
          </div>
        )}
      </div>
      {isPSAModalOpen && <PSABlueprintModal isModalOpen={isPSAModalOpen} onHideHandlr={onHideHandlr} />}
      {isPreContractModalOpen && (
        <PreContractBlueprintModal
          isModalOpen={isPreContractModalOpen}
          onHideHandlr={onHidePreContractHandlr}
        />
      )}
    </>
  );
};

TitleAndEscrow.propTypes = {
  error: errorShape,
  isDismiss: PropTypes.bool,
  getTitleAndEscrow: PropTypes.func,
  listingId: PropTypes.number.isRequired,

  type: PropTypes.string,
  toastState: PropTypes.shape({
    show: PropTypes.bool,
    msg: PropTypes.string,
  }),
  onSubmitReqState: PropTypes.func.isRequired,
  streetAddress: PropTypes.string,
  title: PropTypes.string,
  useCustomTitle: PropTypes.bool,
  city: PropTypes.string,
  locality: PropTypes.string,
  postalCode: PropTypes.string,
  lastSubmitted: PropTypes.string,
  titleType: PropTypes.string,
};

TitleAndEscrow.defaultProps = {
  error: null,
  getTitleAndEscrow: null,
  isDismiss: false,
  type: null,
  toastState: {
    show: false,
    msg: "",
  },
  streetAddress: "",
  title: "",
  useCustomTitle: false,
  city: "",
  locality: "",
  postalCode: "",
  lastSubmitted: "",
  titleType: "",
};

const mapStateToProps = (state) => ({
  error: state.tdpTitleAndEscrowReducer.error,
  toastState: state.tdpTitleAndEscrowReducer.toastState,
  type: state.tdpDetailsReducer.headerDetails.type,
  isDismiss: state.tdpTitleAndEscrowReducer.isDismiss,
  listingId: state.tdpDetailsReducer.headerDetails.listing_id,
  title: state.tdpDetailsReducer.transactionDetails.title,
  useCustomTitle: state.tdpDetailsReducer.transactionDetails.use_custom_title,
  streetAddress: state.tdpDetailsReducer.propertyDetails.street_address,
  city: state.tdpDetailsReducer.propertyDetails.city,
  locality: state.tdpDetailsReducer.propertyDetails.state,
  postalCode: state.tdpDetailsReducer.propertyDetails.zip,
  lastSubmitted: state.tdpTitleAndEscrowReducer.lastSubmitted,
  titleType: state.tdpTitleAndEscrowReducer.titleType,
});
const mapDispatchToProps = (dispatch) => ({
  onSubmitReqState: (data) => dispatch(SubmitReqState(data)),
  getTitleAndEscrow: (listingId) => dispatch(getTitleAndEscrow(listingId)),
});
export default connect(mapStateToProps, mapDispatchToProps)(TitleAndEscrow);
